// ---------------------------------------------------------------------------
// Study-time tracking: seconds studied per UTC day (localStorage), plus a
// small recent-activity log. Signed-in minutes sync to users/{uid}.activeDays
// as { 'YYYY-MM-DD': seconds } via useProgress.
// ---------------------------------------------------------------------------

const LS_MIN = 'hub.minutes.v1';
const LS_ACT = 'hub.activity.v1';
const CAP_DAYS = 400;
export const ACT_CAP = 40;

export type ActivityEvent = { ts: number; tint: string; what: string };
type MinMap = Record<string, number>;

export function dayKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function readMinMap(): MinMap {
  try {
    const raw = localStorage.getItem(LS_MIN);
    const obj = raw ? JSON.parse(raw) : {};
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
    const out: MinMap = {};
    for (const [k, v] of Object.entries(obj)) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(k) && typeof v === 'number' && isFinite(v) && v > 0) out[k] = Math.round(v);
    }
    return out;
  } catch {
    return {};
  }
}

function writeMinMap(m: MinMap) {
  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - CAP_DAYS);
    const cut = dayKey(cutoff);
    const pruned: MinMap = {};
    for (const [k, v] of Object.entries(m)) if (k >= cut) pruned[k] = Math.round(v);
    localStorage.setItem(LS_MIN, JSON.stringify(pruned));
  } catch {
    /* storage unavailable */
  }
}

export function readDaySeconds(): MinMap {
  return readMinMap();
}

/** Union of both stores: legacy `hub.active.v1` days and days with tracked seconds. */
export function activeDateKeys(): Set<string> {
  const set = new Set<string>();
  try {
    const raw = localStorage.getItem('hub.active.v1');
    const arr = raw ? JSON.parse(raw) : [];
    if (Array.isArray(arr)) for (const x of arr) if (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x)) set.add(x);
  } catch {
    /* unreadable */
  }
  for (const [k, v] of Object.entries(readMinMap())) if (v > 0) set.add(k);
  return set;
}

const listeners = new Set<() => void>();

export function subscribeDaySeconds(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function notify() {
  for (const f of listeners) {
    try {
      f();
    } catch {
      /* listener error must not break tracking */
    }
  }
}

export function addStudySeconds(sec: number) {
  if (!sec || sec <= 0) return;
  const m = readMinMap();
  const k = dayKey(new Date());
  m[k] = (m[k] ?? 0) + Math.round(sec);
  writeMinMap(m);
  notify();
}

/** Merge a server map (date -> seconds) taking the max per day. Returns changed. */
export function mergeDaySeconds(remote: MinMap): boolean {
  const local = readMinMap();
  let changed = false;
  for (const [k, v] of Object.entries(remote)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(k) || typeof v !== 'number' || !isFinite(v) || v <= 0) continue;
    if ((local[k] ?? 0) < v) {
      local[k] = Math.round(v);
      changed = true;
    }
  }
  if (changed) {
    writeMinMap(local);
    notify();
  }
  return changed;
}

// ---------- heartbeat tracker ----------

let trackerStarted = false;

/** Counts seconds while the tab is visible. Returns a stop function. */
export function startStudyTracker(): () => void {
  if (trackerStarted || typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  trackerStarted = true;
  const MAX_TICK_SEC = 120; // idle / throttled-tab guard
  let last = Date.now();
  const flush = (visibleOnly: boolean) => {
    const now = Date.now();
    const delta = Math.min(MAX_TICK_SEC, Math.max(0, (now - last) / 1000));
    last = now;
    if (delta >= 1 && (!visibleOnly || document.visibilityState === 'visible')) addStudySeconds(Math.floor(delta));
  };
  const iv = window.setInterval(() => flush(true), 30_000);
  const onVis = () => flush(false); // time since the last flush was visible
  const onLeave = () => flush(false);
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('beforeunload', onLeave);
  return () => {
    window.clearInterval(iv);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('beforeunload', onLeave);
    trackerStarted = false;
  };
}

// ---------- weekly / heatmap math ----------

const DAY_MS = 86_400_000;

/** Monday-aligned grid index of an epoch-day (1970-01-05 was a Monday). */
export function gridIndexOf(eDay: number): number {
  return Math.floor((eDay - 4) / 7);
}
export function rowOf(eDay: number): number {
  return (((eDay - 4) % 7) + 7) % 7;
}

export type HeatCell = { key: string | null; seconds: number; level: number };

/** 12 columns (weeks, Mon-aligned, oldest first) x 7 rows (Mon..Sun). */
export function buildHeatGrid(daySec: MinMap, now = new Date()): { weeks: HeatCell[][]; activeDays: number } {
  const eNow = Math.floor(now.getTime() / DAY_MS);
  const colNow = gridIndexOf(eNow);
  const legacy = activeDateKeys();
  const weeks: HeatCell[][] = [];
  let activeDays = 0;
  for (let w = 11; w >= 0; w--) {
    const col = colNow - w;
    const cells: HeatCell[] = [];
    for (let r = 0; r < 7; r++) {
      const e = col * 7 + 4 + r;
      if (e > eNow) {
        cells.push({ key: null, seconds: 0, level: 0 });
        continue;
      }
      const key = new Date(e * DAY_MS).toISOString().slice(0, 10);
      const s = daySec[key] ?? 0;
      const active = s > 0 || legacy.has(key);
      if (active) activeDays++;
      cells.push({ key, seconds: s, level: s <= 0 ? (active ? 1 : 0) : levelOf(s) });
    }
    weeks.push(cells);
  }
  return { weeks, activeDays };
}

/** 0..4 heat levels from seconds studied that day. */
export function levelOf(sec: number): number {
  if (sec >= 3600) return 4;
  if (sec >= 1800) return 3;
  if (sec >= 600) return 2;
  if (sec > 0) return 1;
  return 0;
}

export type WeekBar = { minutes: number; label: string; future: boolean };

/** Minutes studied per Monday-aligned week, last 12 weeks, oldest first. */
export function buildWeekBars(daySec: MinMap, now = new Date()): WeekBar[] {
  const eNow = Math.floor(now.getTime() / DAY_MS);
  const colNow = gridIndexOf(eNow);
  const out: WeekBar[] = [];
  for (let w = 11; w >= 0; w--) {
    const col = colNow - w;
    let sec = 0;
    let has = false;
    for (let r = 0; r < 7; r++) {
      const e = col * 7 + 4 + r;
      if (e > eNow) continue;
      has = true;
      sec += daySec[new Date(e * DAY_MS).toISOString().slice(0, 10)] ?? 0;
    }
    const start = new Date((col * 7 + 4) * DAY_MS);
    out.push({
      minutes: Math.round(sec / 60),
      label: `Week of ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`,
      future: !has
    });
  }
  return out;
}

/** '21h 00m' / '1h 45m' / '40m' */
export function fmtHm(totalSec: number): string {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
}

/** '2h 34m / week' style for avg over weeks that actually exist. */
export function fmtAvgPerWeek(bars: WeekBar[]): string {
  const live = bars.filter(b => !b.future);
  if (!live.length) return '0m';
  const avgMin = live.reduce((n, b) => n + b.minutes, 0) / live.length;
  return fmtHm(avgMin * 60);
}

// ---------- recent activity log (local) ----------

export function logActivity(tint: string, what: string) {
  try {
    const raw = localStorage.getItem(LS_ACT);
    const arr: ActivityEvent[] = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(arr) ? arr.filter(e => e && typeof e.what === 'string' && typeof e.ts === 'number') : [];
    list.unshift({ ts: Date.now(), tint, what: what.slice(0, 160) });
    localStorage.setItem(LS_ACT, JSON.stringify(list.slice(0, ACT_CAP)));
  } catch {
    /* storage unavailable */
  }
}

export function readActivity(): ActivityEvent[] {
  try {
    const raw = localStorage.getItem(LS_ACT);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((e: unknown): e is ActivityEvent => !!e && typeof (e as ActivityEvent).ts === 'number' && typeof (e as ActivityEvent).what === 'string')
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 5);
  } catch {
    return [];
  }
}

export function whenLabel(ts: number, now = new Date()): string {
  const t = new Date(ts);
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  const daysAgo = Math.floor((endOfDay.getTime() - t.getTime()) / DAY_MS);
  const hm = t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (daysAgo <= 0) return `Today, ${hm}`;
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo < 7) return `${daysAgo} days ago`;
  return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
